import { useState } from 'react';
import { supabase } from '../../config/supabase';
import { TaskFormData } from '../../types/task';
import { useStore } from '../../store/useStore';
import { logger } from '../../utils/logger';
import toast from 'react-hot-toast';

export const useLocalTaskManagement = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { currentUser, addTask } = useStore();

  const createTask = async (formData: TaskFormData) => {
    if (!currentUser) {
      toast.error('You must be logged in to create tasks');
      return;
    }

    try {
      setIsLoading(true);
      logger.info('Creating task:', formData);

      // Create task in Supabase
      const { data: task, error: taskError } = await supabase
        .from('tasks')
        .insert({
          title: formData.title,
          description: formData.description,
          assigned_to: formData.assignedTo,
          assigned_by: currentUser.id,
          department: formData.department,
          priority: formData.priority,
          due_date: new Date(`${formData.dueDate}T${formData.dueTime}`).toISOString(),
          status: 'PENDING'
        })
        .select()
        .single();

      if (taskError) throw taskError;
      if (!task) throw new Error('No task data returned');

      logger.info('Task created:', task);

      // Create subtasks if any
      if (formData.subtasks?.length) {
        const { error: subtasksError } = await supabase
          .from('subtasks')
          .insert(
            formData.subtasks.map(st => ({
              task_id: task.id,
              title: st.title,
              description: st.description,
              due_date: st.hasDueDate ? new Date(`${st.dueDate}T${st.dueTime}`).toISOString() : null,
              status: 'PENDING'
            }))
          );

        if (subtasksError) throw subtasksError;
      }

      // Create notification message
      const { error: messageError } = await supabase
        .from('messages')
        .insert({
          type: 'DIRECT',
          priority: formData.priority,
          title: `New Task Assignment: ${formData.title}`,
          content: formData.description,
          sender_id: currentUser.id,
          departments: [formData.department],
          is_direct: true
        });

      if (messageError) {
        logger.error('Failed to create notification message:', messageError);
      }

      // Add task to local store
      const mappedTask = {
        id: task.id,
        title: task.title,
        description: task.description || '',
        assignedTo: task.assigned_to,
        assignedBy: task.assigned_by,
        department: task.department,
        priority: task.priority,
        dueDate: new Date(task.due_date),
        status: task.status,
        createdAt: new Date(task.created_at),
        updatedAt: new Date(task.updated_at),
        acknowledged: task.acknowledged,
        comments: [],
        subtasks: []
      };

      addTask(mappedTask);
      toast.success('Task created successfully');
      return mappedTask;

    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create task';
      logger.error('Task creation error:', error);
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createTask,
    isLoading
  };
};